import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Input, message, Modal, Radio } from 'antd';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';

import ModalFormFooter from 'components/Form/ModalFormFooter';
import FormItem from 'components/Form/StyledFormItem';
import { FakeUploadButton } from 'components/Layout/Form';
import Label from 'components/Text/Label';
import SectionLabel from 'components/Text/SectionLabel';
import { convertUrlToFile } from 'containers/BDO/utils';

import { AddEditCreditCardModalProps, ICreditCard, UpdateCreditCardFormValues } from '../types';
import { createCreditCard, getCreditCard, updateCreditCard } from './actions';
import { BRAND_LABEL_KEYS, BRAND_LABELS, CARD_TYPES_REQUIRING_BIN, CardType } from './constants';

interface FormValues {
  name: string;
  card_type: string;
  product_code: string;
  image?: any;
  images?: any[];
  bin_number?: string | null;
  minimum_points?: number | string | null;
}

type SagaErrorPayload =
  | { data?: { errors?: string[] } } // createCreditCard shape (full response)
  | { errors?: string[] }; // updateCreditCard shape (response.data)

const validationSchema = yup.object().shape({
  name: yup.string().required('Card Name is required'),
  product_code: yup
    .string()
    .required('Product Code is required')
    .matches(/^[a-zA-Z0-9]+$/, 'Product Code only allows alphanumeric characters'),
  bin_number: yup
    .string()
    .nullable()
    .when('card_type', {
      is: (v: string) => CARD_TYPES_REQUIRING_BIN.includes(v as CardType),
      then: s =>
        s
          .required('BIN Number is required')
          .matches(/^\d{6}$/, 'BIN Number must be exactly 6 digits'),
      otherwise: s => s.notRequired().nullable()
    }),
  minimum_points: yup
    .number()
    .nullable()
    .transform((v, orig) => (orig === '' || orig == null ? null : v))
    .when('card_type', {
      is: (v: string) => CARD_TYPES_REQUIRING_BIN.includes(v as CardType),
      then: n =>
        n
          .typeError('Minimum points must be an integer')
          .integer('Minimum points must be an integer')
          .min(1, 'Min Redemption must be at least 1 point')
          .required('Minimum points is required'),
      otherwise: n => n.notRequired().nullable()
    })
});

const AddEditCreditCardModal: React.FC<AddEditCreditCardModalProps> = ({
  selectedCreditCard = null,
  modalOpen,
  closeModal,
  isEdit = false
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchedCreditCardData: ICreditCard | null = useSelector(
    (state: { creditCards: { creditCard: ICreditCard | null } }) => state.creditCards.creditCard
  );
  useEffect(() => {
    if (modalOpen && selectedCreditCard) {
      dispatch(getCreditCard(selectedCreditCard));
    }
  }, [modalOpen, selectedCreditCard, dispatch]);

  const initialValues = {
    card_type: fetchedCreditCardData ? fetchedCreditCardData.card_type : 'amex',
    name:
      isEdit && fetchedCreditCardData
        ? fetchedCreditCardData.name
        : !isEdit && selectedCreditCard
        ? fetchedCreditCardData
          ? `Copy of ${fetchedCreditCardData.name}`
          : ''
        : '',
    product_code: fetchedCreditCardData ? fetchedCreditCardData.product_code : '',
    image: fetchedCreditCardData?.image ? fetchedCreditCardData.image : null,
    bin_number: fetchedCreditCardData?.bin_number ?? '',
    minimum_points: fetchedCreditCardData?.minimum_points ?? null
  };

  const onSave = async (
    values: FormValues,
    {
      setSubmitting,
      resetForm,
      setFieldError
    }: {
      setSubmitting: (isSubmitting: boolean) => void;
      resetForm: () => void;
      setFieldError: (field: string, message: string) => void;
    }
  ) => {
    setSubmitting(true);

    const onSuccess = () => {
      setSubmitting(false);
      resetForm();
      closeModal();
      if (!isEdit) {
        navigate(`/p/credit_card_redemption_settings/credit-cards/list`);
      }
    };

    const onError = (err: SagaErrorPayload) => {
      setSubmitting(false);
      // normalize: create passes full response, update passes response.data
      const payload = (err as { data?: { errors?: string[] } })?.data ?? err;
      const errors: string[] = (payload as { errors?: string[] })?.errors ?? [];
      if (!Array.isArray(errors) || errors.length === 0) return;

      errors.forEach(msg => {
        if (typeof msg !== 'string') return;
        const lower = msg.toLowerCase();
        if (lower.startsWith('bin number ')) {
          setFieldError('bin_number', msg);
        } else if (lower.startsWith('minimum points ')) {
          setFieldError('minimum_points', msg);
        } else if (lower.startsWith('product code ')) {
          setFieldError('product_code', msg);
        } else if (lower.startsWith('name ')) {
          setFieldError('name', msg);
        } else {
          message.error(msg);
        }
      });
    };

    const requiresBin = CARD_TYPES_REQUIRING_BIN.includes(values.card_type as CardType);
    const shapedValues = {
      ...values,
      bin_number: requiresBin ? values.bin_number : null,
      minimum_points: requiresBin ? values.minimum_points : null
    };

    if (isEdit && selectedCreditCard) {
      dispatch(
        updateCreditCard(
          { ...(shapedValues as UpdateCreditCardFormValues), id: selectedCreditCard },
          onSuccess,
          onError
        )
      );
    } else {
      if (!shapedValues.image?.file) {
        if (shapedValues.image?.url) {
          try {
            const fileFromUrl = await convertUrlToFile(shapedValues.image.url);
            shapedValues.images = [{ file: fileFromUrl, filename: shapedValues.name }];
          } catch (error) {
            message.error('The image from the provided link could not be processed.');
            setSubmitting(false);
            return;
          }
        } else {
          message.error('An image file is required.');
          setSubmitting(false);
          return;
        }
      }
      dispatch(createCreditCard(shapedValues, onSuccess, onError));
    }
  };

  return (
    <Modal
      maskClosable={false}
      destroyOnClose
      open={modalOpen}
      title={isEdit ? 'Update Credit Card' : 'Add New Credit Card'}
      footer={null}
      onCancel={closeModal}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSave}
        enableReinitialize
      >
        {({ isSubmitting, resetForm, setFieldValue, values }) => (
          <Form>
            <Field name="card_type">
              {({ field }) => (
                <FormItem label="Card Brand" required>
                  <Radio.Group
                    {...field}
                    onChange={e => {
                      const next = e.target.value as string;
                      field.onChange(e);
                      if (!CARD_TYPES_REQUIRING_BIN.includes(next as CardType)) {
                        setFieldValue('bin_number', null);
                        setFieldValue('minimum_points', null);
                      }
                    }}
                  >
                    {BRAND_LABEL_KEYS.map(key => (
                      <Radio key={key} value={key}>
                        {BRAND_LABELS[key]}
                      </Radio>
                    ))}
                  </Radio.Group>
                </FormItem>
              )}
            </Field>
            <Field name="name">
              {({ field, meta }) => {
                const hasError = meta.error && meta.touched;
                return (
                  <>
                    <FormItem
                      label="Card Name"
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError ? meta.error : undefined}
                      required
                    >
                      <Input allowClear placeholder="Credit Card Name" {...field} />
                    </FormItem>
                  </>
                );
              }}
            </Field>
            <Field name="product_code">
              {({ field, meta }) => {
                const hasError = meta.error && meta.touched;
                return (
                  <>
                    <FormItem
                      label="Product Code"
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError ? meta.error : undefined}
                      required
                    >
                      <Input
                        allowClear
                        placeholder="Unique Short Code Representing the Card"
                        {...field}
                        onChange={e =>
                          setFieldValue('product_code', e.target.value.replace(/[^a-zA-Z0-9]/g, ''))
                        }
                      />
                    </FormItem>
                  </>
                );
              }}
            </Field>
            {CARD_TYPES_REQUIRING_BIN.includes(values.card_type as CardType) ? (
              <>
                <Field name="bin_number">
                  {({ field, meta }) => {
                    const hasError = meta.error && meta.touched;
                    return (
                      <FormItem
                        label="BIN Number"
                        validateStatus={hasError ? 'error' : undefined}
                        hasFeedback={!hasError && meta.touched}
                        help={hasError ? meta.error : undefined}
                        required
                      >
                        <Input
                          allowClear
                          placeholder="6-digit BIN Number"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormItem>
                    );
                  }}
                </Field>
                <Field name="minimum_points">
                  {({ field, meta }) => {
                    const hasError = meta.error && meta.touched;
                    return (
                      <FormItem
                        label="Min Redemption"
                        validateStatus={hasError ? 'error' : undefined}
                        hasFeedback={!hasError && meta.touched}
                        help={hasError ? meta.error : undefined}
                        required
                      >
                        <Input
                          allowClear
                          type="number"
                          min={1}
                          placeholder="Minimum Points for Redemption"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormItem>
                    );
                  }}
                </Field>
              </>
            ) : null}
            <Field name="image">
              {({ field }) => {
                return (
                  <>
                    <FormItem label="Upload Image" required>
                      <div style={{ display: 'block', textAlign: 'center' }}>
                        <label htmlFor="image_uploads">
                          <FakeUploadButton>Upload Image</FakeUploadButton>
                        </label>
                      </div>
                      <input
                        id="image_uploads"
                        style={{
                          opacity: 0,
                          width: 0,
                          height: 0,
                          position: 'absolute',
                          transform: 'translateX(-100vw)'
                        }}
                        type="file"
                        accept="image/png"
                        onChange={e => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.type !== 'image/png') {
                              message.error('Only PNG files are allowed.');
                              return;
                            }
                            const objectUrl = URL.createObjectURL(file);
                            setFieldValue(field.name, { file, url: objectUrl });
                          }
                        }}
                      />
                      {field.value && field.value.url && (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginTop: '10px'
                          }}
                        >
                          <img
                            src={field.value.url}
                            style={{
                              maxWidth: 'min(120px, 15vw)',
                              maxHeight: 'min(80px, 10vh)',
                              height: 'auto',
                              objectFit: 'contain',
                              borderRadius: '4px',
                              padding: '5px'
                            }}
                            alt="Credit card preview"
                          />
                        </div>
                      )}
                    </FormItem>
                  </>
                );
              }}
            </Field>
            <div>
              <SectionLabel aCenter fullwidth>
                <Label>(w x h png only)</Label>
              </SectionLabel>
            </div>
            <ModalFormFooter>
              <Button
                type="default"
                onClick={() => {
                  resetForm();
                  closeModal();
                }}
              >
                Cancel
              </Button>
              <Button htmlType="submit" loading={isSubmitting} type="primary">
                Save
              </Button>
            </ModalFormFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default AddEditCreditCardModal;
